'use client';

import { createClient } from '@/lib/supabase/client';


// ─── Raw Test Metrics ─────────────────────────────────────────────────────────

export interface ReactionTestMetrics {
  reactionTimes: number[];       // ms per correct hit
  errors: number;                // false positives (hit on red)
  misses: number;                // missed green stimuli
  totalStimuli: number;
}

export interface WorkingMemoryMetrics {
  accuracy: number;              // 0-1
  responseTimes: number[];       // ms per response
  totalTrials: number;
  correctTrials: number;
}

export interface FlexibilityMetrics {
  switchingLatencies: number[];  // ms for switch trials
  stayLatencies: number[];       // ms for stay trials
  postSwitchErrors: number;
  totalSwitches: number;
}

export interface BrainCheckRawMetrics {
  reaction?: ReactionTestMetrics;
  workingMemory?: WorkingMemoryMetrics;
  flexibility?: FlexibilityMetrics;
  durationMs: number;
  completedTests: string[];
}

// ─── Derived Scores ───────────────────────────────────────────────────────────

export interface BrainCheckScores {
  focus: number;           // 0-100
  mentalEnergy: number;    // 0-100
  stability: number;       // 0-100
  flexibility: number;     // 0-100
  overall: number;         // 0-100
}

export interface BrainCheckBaseline {
  focus: number;
  mentalEnergy: number;
  stability: number;
  flexibility: number;
  overall: number;
  sessionCount: number;
}

export interface BrainCheckResult {
  scores: BrainCheckScores;
  deltas?: Partial<BrainCheckScores>;   // vs baseline
  confidence: number;                   // 0-1
  cognitiveState: CognitiveState;
  insight: string;
  recommendation: string;
  rawMetrics: BrainCheckRawMetrics;
  sessionId?: string;
  timestamp: string;
}

// ─── Cognitive State ──────────────────────────────────────────────────────────

export type CognitiveStateLabel = 'Focused' | 'Fatigued' | 'Stressed' | 'Recovering' | 'Balanced' | 'Elevated';

export interface CognitiveState {
  label: CognitiveStateLabel;
  confidence: number;   // 0-1
  explanation: string;
  sources: string[];    // which data sources contributed
}

// ─── Unified Data Model ───────────────────────────────────────────────────────

export interface UnifiedCognitiveSession {
  user_id: string;
  timestamp: string;
  source: 'EEG' | 'PPG' | 'Wearable' | 'BrainCheck';
  metrics: Record<string, number>;
  derived_state: CognitiveState;
  scores?: BrainCheckScores;
}

// ─── Scoring Engine ───────────────────────────────────────────────────────────

export function computeBrainCheckScores(raw: BrainCheckRawMetrics): BrainCheckScores {
  let focus = 50;
  let mentalEnergy = 50;
  let stability = 50;
  let flexibility = 50;

  // ── Focus: from reaction test ──
  if (raw.reaction) {
    const r = raw.reaction;
    const avgRT = r.reactionTimes.length > 0
      ? r.reactionTimes.reduce((a, b) => a + b, 0) / r.reactionTimes.length
      : 600;
    // Faster RT → higher focus. 200ms = 100, 800ms = 0
    const rtScore = Math.max(0, Math.min(100, ((800 - avgRT) / 600) * 100));
    const errorRate = r.totalStimuli > 0 ? (r.errors + r.misses) / r.totalStimuli : 0.5;
    const accuracyScore = (1 - errorRate) * 100;
    focus = Math.round(rtScore * 0.5 + accuracyScore * 0.5);
  }

  // ── Mental Energy: from working memory ──
  if (raw.workingMemory) {
    const wm = raw.workingMemory;
    const avgRT = wm.responseTimes.length > 0
      ? wm.responseTimes.reduce((a, b) => a + b, 0) / wm.responseTimes.length
      : 1500;
    const rtScore = Math.max(0, Math.min(100, ((3000 - avgRT) / 2500) * 100));
    const accScore = wm.accuracy * 100;
    mentalEnergy = Math.round(accScore * 0.6 + rtScore * 0.4);
  }

  // ── Stability: reaction time variability ──
  if (raw.reaction && raw.reaction.reactionTimes.length >= 3) {
    const rts = raw.reaction.reactionTimes;
    const mean = rts.reduce((a, b) => a + b, 0) / rts.length;
    const variance = rts.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / rts.length;
    const cv = Math.sqrt(variance) / mean; // coefficient of variation
    // Low CV → high stability. CV < 0.1 = 100, CV > 0.5 = 0
    stability = Math.round(Math.max(0, Math.min(100, ((0.5 - cv) / 0.4) * 100)));
  }

  // ── Flexibility: from switching task ──
  if (raw.flexibility) {
    const f = raw.flexibility;
    const avgSwitch = f.switchingLatencies.length > 0
      ? f.switchingLatencies.reduce((a, b) => a + b, 0) / f.switchingLatencies.length
      : 1200;
    const avgStay = f.stayLatencies.length > 0
      ? f.stayLatencies.reduce((a, b) => a + b, 0) / f.stayLatencies.length
      : 800;
    const switchCost = Math.max(0, avgSwitch - avgStay);
    // Low switch cost → high flexibility. 0ms = 100, 600ms+ = 0
    const latencyScore = Math.max(0, Math.min(100, ((600 - switchCost) / 600) * 100));
    const errorScore = f.totalSwitches > 0
      ? Math.max(0, (1 - f.postSwitchErrors / f.totalSwitches) * 100)
      : 50;
    flexibility = Math.round(latencyScore * 0.6 + errorScore * 0.4);
  }

  const overall = Math.round((focus + mentalEnergy + stability + flexibility) / 4);

  return { focus, mentalEnergy, stability, flexibility, overall };
}

export function computeDeltas(
  scores: BrainCheckScores,
  baseline: BrainCheckBaseline
): Partial<BrainCheckScores> {
  return {
    focus: scores.focus - baseline.focus,
    mentalEnergy: scores.mentalEnergy - baseline.mentalEnergy,
    stability: scores.stability - baseline.stability,
    flexibility: scores.flexibility - baseline.flexibility,
    overall: scores.overall - baseline.overall,
  };
}

export function computeConfidence(raw: BrainCheckRawMetrics): number {
  let score = 0;
  let total = 0;
  if (raw.reaction) {
    total += 1;
    if (raw.reaction.reactionTimes.length >= 5) score += 1;
    else score += raw.reaction.reactionTimes.length / 5;
  }
  if (raw.workingMemory) {
    total += 1;
    if (raw.workingMemory.totalTrials >= 6) score += 1;
    else score += raw.workingMemory.totalTrials / 6;
  }
  if (raw.flexibility) {
    total += 1;
    if (raw.flexibility.totalSwitches >= 4) score += 1;
    else score += raw.flexibility.totalSwitches / 4;
  }
  return total > 0 ? score / total : 0;
}

// ─── Cognitive State Engine ───────────────────────────────────────────────────

export function deriveCognitiveState(
  scores: BrainCheckScores,
  eegAvailable = false,
  hrvScore?: number,
  sleepScore?: number
): CognitiveState {
  const sources: string[] = ['Brain Check'];
  if (eegAvailable) sources.unshift('EEG');
  if (hrvScore != null) sources.push('HRV');
  if (sleepScore != null) sources.push('Sleep');

  const { focus, mentalEnergy, stability, flexibility } = scores;

  // EEG has highest weight if available (simulated here via scores)
  let label: CognitiveStateLabel;
  let explanation: string;
  let confidence: number;

  if (focus >= 70 && mentalEnergy >= 65 && stability >= 65) {
    label = 'Focused';
    explanation = 'High attention, strong mental energy, and consistent response patterns indicate a focused cognitive state.';
    confidence = 0.85;
  } else if (mentalEnergy < 45 && stability < 50) {
    label = 'Fatigued';
    explanation = 'Reduced mental energy and increased response variability suggest cognitive fatigue.';
    confidence = 0.80;
  } else if (focus < 50 && stability < 45 && (hrvScore == null || hrvScore < 50)) {
    label = 'Stressed';
    explanation = 'Elevated error rates and high response variability are consistent with a stressed state.';
    confidence = 0.75;
  } else if (mentalEnergy >= 55 && focus < 60) {
    label = 'Recovering';
    explanation = 'Energy levels are rebuilding but sustained attention is not yet at peak — a recovery phase.';
    confidence = 0.70;
  } else if (focus >= 60 && mentalEnergy >= 60 && flexibility >= 60) {
    label = 'Elevated';
    explanation = 'Above-average performance across all domains — your brain is operating at an elevated level today.';
    confidence = 0.80;
  } else {
    label = 'Balanced';
    explanation = 'Scores are within normal range across all cognitive domains — a balanced state.';
    confidence = 0.65;
  }

  // Adjust confidence based on HRV/sleep alignment
  if (hrvScore != null) {
    const hrvAligned = (label === 'Stressed' && hrvScore < 50) || (label === 'Focused' && hrvScore >= 60);
    if (hrvAligned) confidence = Math.min(1, confidence + 0.1);
  }

  return { label, confidence, explanation, sources };
}

// ─── Static Insights ─────────────────────────────────────────────────────────

export function generateStaticInsight(scores: BrainCheckScores, state: CognitiveState): { insight: string; recommendation: string } {
  const { focus, mentalEnergy, stability, flexibility } = scores;

  if (state.label === 'Focused') {
    return {
      insight: 'Your brain is in peak condition right now — attention and processing speed are both elevated.',
      recommendation: 'Use this window for demanding cognitive tasks: deep work, learning, or complex problem-solving.',
    };
  }
  if (state.label === 'Fatigued') {
    return {
      insight: 'Your cognitive resources are running low — reaction time and memory performance are below your baseline.',
      recommendation: 'Take a 20-minute break, hydrate, and consider a short walk before your next demanding task.',
    };
  }
  if (state.label === 'Stressed') {
    return {
      insight: 'Response variability and error rates suggest your brain is under load — likely stress or distraction.',
      recommendation: 'Try 5 minutes of box breathing (4-4-4-4) to reset your nervous system before continuing.',
    };
  }
  if (state.label === 'Recovering') {
    return {
      insight: 'Your energy is rebuilding but sustained focus is still catching up — you\'re in a recovery arc.',
      recommendation: 'Light cognitive tasks are fine now. Avoid high-stakes decisions for another 30–60 minutes.',
    };
  }
  if (state.label === 'Elevated') {
    return {
      insight: 'Exceptional performance across all domains today — your cognitive flexibility and focus are both above average.',
      recommendation: 'This is a great time for creative work, strategic planning, or tackling complex challenges.',
    };
  }
  // Balanced
  const weakest = [
    { key: 'focus', val: focus },
    { key: 'mentalEnergy', val: mentalEnergy },
    { key: 'stability', val: stability },
    { key: 'flexibility', val: flexibility },
  ].sort((a, b) => a.val - b.val)[0];

  return {
    insight: `Your brain is in a balanced state today. ${weakest.key === 'flexibility' ? 'Cognitive switching speed is the area with most room to grow.' : weakest.key === 'stability' ? 'Response consistency could be improved with regular practice.' : 'All domains are within normal range.'}`,
    recommendation: `Focus on improving ${weakest.key === 'focus' ? 'sustained attention' : weakest.key === 'mentalEnergy' ? 'working memory' : weakest.key === 'stability' ? 'consistency' : 'mental flexibility'} with targeted exercises.`,
  };
}

// ─── Supabase Persistence ─────────────────────────────────────────────────────

export async function saveBrainCheckSession(
  userId: string,
  result: BrainCheckResult
): Promise<string | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('brain_check_sessions')
    .insert({
      user_id: userId,
      timestamp: result.timestamp,
      source: 'BrainCheck',
      raw_metrics: result.rawMetrics,
      focus_score: result.scores.focus,
      mental_energy_score: result.scores.mentalEnergy,
      stability_score: result.scores.stability,
      flexibility_score: result.scores.flexibility,
      overall_score: result.scores.overall,
      delta_focus: result.deltas?.focus ?? null,
      delta_mental_energy: result.deltas?.mentalEnergy ?? null,
      delta_stability: result.deltas?.stability ?? null,
      delta_flexibility: result.deltas?.flexibility ?? null,
      delta_overall: result.deltas?.overall ?? null,
      confidence: result.confidence,
      cognitive_state: result.cognitiveState.label,
      state_confidence: result.cognitiveState.confidence,
      state_explanation: result.cognitiveState.explanation,
      insight: result.insight,
      recommendation: result.recommendation,
      duration_ms: result.rawMetrics.durationMs,
      completed_tests: result.rawMetrics.completedTests,
    })
    .select('id')
    .single();

  if (error) {
    console.error('Brain check save error:', error);
    return null;
  }
  return data?.id ?? null;
}

export async function getBrainCheckHistory(
  userId: string,
  limit = 30
): Promise<any[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('brain_check_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('timestamp', { ascending: false })
    .limit(limit);

  if (error) return [];
  return data ?? [];
}

export async function getBrainCheckBaseline(userId: string): Promise<BrainCheckBaseline | null> {
  const sessions = await getBrainCheckHistory(userId, 10);
  if (sessions.length === 0) return null;

  const avg = (key: string) =>
    Math.round(sessions.reduce((a: number, s: any) => a + (s[key] ?? 0), 0) / sessions.length);

  return {
    focus: avg('focus_score'),
    mentalEnergy: avg('mental_energy_score'),
    stability: avg('stability_score'),
    flexibility: avg('flexibility_score'),
    overall: avg('overall_score'),
    sessionCount: sessions.length,
  };
}

export async function getBrainCheckCohortStats(cohortId: string): Promise<any> {
  const supabase = createClient();
  // Join brain_check_sessions with cohort_members
  const { data, error } = await supabase
    .from('brain_check_sessions')
    .select(`
      focus_score, mental_energy_score, stability_score, flexibility_score, overall_score,
      cognitive_state, timestamp, user_id,
      cohort_members!inner(cohort_id)
    `)
    .eq('cohort_members.cohort_id', cohortId)
    .order('timestamp', { ascending: false })
    .limit(200);

  if (error || !data) return null;

  const avg = (key: string) =>
    data.length > 0
      ? Math.round(data.reduce((a: number, s: any) => a + (s[key] ?? 0), 0) / data.length)
      : 0;

  const stateCounts: Record<string, number> = {};
  data.forEach((s: any) => {
    stateCounts[s.cognitive_state] = (stateCounts[s.cognitive_state] || 0) + 1;
  });

  // Outliers: overall_score < avg - 20
  const avgOverall = avg('overall_score');
  const outliers = data.filter((s: any) => s.overall_score < avgOverall - 20);

  return {
    sessionCount: data.length,
    avgFocus: avg('focus_score'),
    avgMentalEnergy: avg('mental_energy_score'),
    avgStability: avg('stability_score'),
    avgFlexibility: avg('flexibility_score'),
    avgOverall,
    stateCounts,
    outlierCount: outliers.length,
    recentSessions: data.slice(0, 20),
  };
}
